import { comActivityTypes } from 'lib/comActivityTypes'

/**
 * The Activities feed's filter groups.
 *
 * Chips rather than the old dropdown, so the Activities tab filters the way the
 * PMS tab does: additive, always visible, and with the counts on show rather
 * than hidden behind a menu.
 */
/**
 * Calls, texts, emails, follow ups, notes and tasks: everything a person did,
 * whether they said it to the patient or wrote it down afterwards. These were
 * two chips, which asked the reader to know in advance whether what they were
 * looking for had been filed as a communication or as a note. The distinction
 * is the PMS's, not the reader's.
 *
 * Not `activities`: that string is already the Timeline's own
 * `RECORD_ACTIVITIES` layer, and a shared value would make this chip read as
 * permanently on.
 */
export const ACTIVITY_ACTIVITIES = `contact-activities`
/**
 * Field changes, patient linking and milestones: what the record did rather
 * than what a person did.
 *
 * Milestones used to be a group of their own, pinned on with no chip. A group
 * that is selected but has no chip means the stored selection is never empty,
 * so every visible chip could be switched off while the feed still showed
 * milestones alone, which read as the contact's calls having gone missing.
 * Every group has a chip now, and milestones ride this one.
 */
export const ACTIVITY_UPDATES = `updates`
/**
 * Anything the named groups do not claim: custom activity types, and newer ones
 * like AI Workflow. The old filter was a deny-list, so these always showed; an
 * allow-list would have made them vanish silently. They get their own chip so
 * the row stays honest about what is on screen.
 */
export const ACTIVITY_OTHER = `other`

export type ActivityKind = typeof ACTIVITY_ACTIVITIES | typeof ACTIVITY_UPDATES | typeof ACTIVITY_OTHER

export const ACTIVITY_KIND_TYPES: Record<ActivityKind, string[]> = {
  [ACTIVITY_ACTIVITIES]: [...comActivityTypes, `FollowUp`, `Note`, `Task`],
  [ACTIVITY_UPDATES]: [`Patient`, `Change`, `Milestone`],
  // Defined by exclusion, so it has no fixed type list.
  [ACTIVITY_OTHER]: [],
}

export const ACTIVITY_KIND_LABELS: Record<ActivityKind, string> = {
  [ACTIVITY_ACTIVITIES]: `Activities`,
  [ACTIVITY_UPDATES]: `Updates`,
  [ACTIVITY_OTHER]: `Other`,
}

/** Display order. */
export const ACTIVITY_KINDS: ActivityKind[] = [ACTIVITY_ACTIVITIES, ACTIVITY_UPDATES, ACTIVITY_OTHER]

/**
 * Only Updates stays off. It is system bookkeeping (field changes, patient
 * linking, milestones) rather than something a person did, and it drowns the
 * feed.
 */
export const DEFAULT_ACTIVITY_KINDS: ActivityKind[] = [ACTIVITY_ACTIVITIES, ACTIVITY_OTHER]

type HasType = { type?: string | null }

const NAMED_KINDS = ACTIVITY_KINDS.filter((kind) => kind !== ACTIVITY_OTHER)

export function activityMatchesKind(activity: HasType, kind: ActivityKind): boolean {
  if (kind === ACTIVITY_OTHER) return !NAMED_KINDS.some((named) => activityMatchesKind(activity, named))
  return !!activity.type && ACTIVITY_KIND_TYPES[kind].includes(activity.type)
}

/** Which chips would reveal this activity. Never empty: Other is the catch-all. */
export function kindsForActivity(activity: HasType): ActivityKind[] {
  return ACTIVITY_KINDS.filter((kind) => activityMatchesKind(activity, kind))
}

export function filterActivitiesByKinds<T extends HasType>(activities: T[], kinds: ActivityKind[]): T[] {
  return activities.filter((activity) => kinds.some((kind) => activityMatchesKind(activity, kind)))
}

/**
 * Per-chip totals. Each number answers "how much would this chip bring in",
 * which is the question the reader is actually asking.
 */
export function countActivitiesByKind(activities: HasType[]): Record<ActivityKind, number> {
  const counts = Object.fromEntries(ACTIVITY_KINDS.map((kind) => [kind, 0])) as Record<ActivityKind, number>
  for (const activity of activities) {
    for (const kind of kindsForActivity(activity)) counts[kind] += 1
  }
  return counts
}
